<script setup lang="ts">
/**
 * LiveCampaignBanner — slim 48px bar below the header when a campaign is
 * live. Tap target routes to /discovery#campaign. Hidden when no campaign
 * is in flight. Does NOT modify the KPI row.
 *
 * bead: lucky-war-kz6
 */
import { ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import type { CampaignImpactItem } from '@/api';
import { fetchCampaignImpact } from '@/api';
import { apiBrand, lensFromQuery } from '@/lib/brand';

const live = ref<CampaignImpactItem | null>(null);
const route = useRoute();

async function load() {
  try {
    const res = await fetchCampaignImpact(undefined, apiBrand(lensFromQuery(route.query.brand)));
    live.value = res.items.find((i) => i.phase === 'live') ?? null;
  } catch {
    // Banner is decorative — never break the dashboard on fetch failure.
    live.value = null;
  }
}

onMounted(load);
watch(() => route.query.brand, load);
</script>

<template>
  <RouterLink
    v-if="live"
    to="/discovery#campaign"
    class="banner"
    aria-label="แคมเปญที่กำลังดำเนินอยู่"
  >
    <span class="banner__dot" aria-hidden="true" />
    <span class="banner__text">
      กำลังมีแคมเปญ — {{ live.name_th }} (วันที่ {{ live.day_of_window }} จาก {{ live.total_days }})
    </span>
    <span class="banner__cta" aria-hidden="true">→</span>
  </RouterLink>
</template>

<style scoped>
.banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--surface-green-soft);
  border-bottom: 1px solid var(--line);
  text-decoration: none;
  min-height: 48px;
  box-sizing: border-box;
}

.banner__dot {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-round);
  background: var(--color-good);
  flex-shrink: 0;
  animation: banner-pulse 1.6s ease-in-out infinite;
}

@keyframes banner-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.banner__text {
  flex: 1;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text);
  font-family: 'Sarabun', sans-serif;
}

.banner__cta {
  font-size: 1rem;
  color: var(--text-dim);
}
</style>
